import { InMemoryWebStorage, WebStorageStateStore, type UserManagerSettings } from "oidc-client-ts";
import { runtimeConfig } from "@/shared/config/runtimeConfig";

/**
 * Provider-agnostic OIDC configuration.
 *
 * The SPA is a public client using the Authorization Code flow with PKCE.
 * Access tokens are renewed silently via the refresh-token grant (the
 * `offline_access` scope must be present in {@link runtimeConfig.OIDC_SCOPE}),
 * so no IdP-specific renewal workarounds are needed. Nothing here is tied to a
 * particular identity provider — Entra-specific quirks (e.g. a `/v2.0/`
 * discovery path) are expressed purely as config values.
 */

export interface OidcConfigInputs {
  authority: string;
  /** Explicit discovery URL; empty means derive `{authority}/.well-known/openid-configuration`. */
  metadataUrl: string;
  clientId: string;
  scope: string;
  /** Absolute app origin URL used for the redirect and post-logout targets. */
  appBaseUri: string;
}

/** True once the minimum config needed to start a login is present. */
export function isOidcConfigured(authority: string, clientId: string): boolean {
  return Boolean(authority.trim()) && Boolean(clientId.trim());
}

/**
 * Resolves the store the user (access + refresh token) is persisted in.
 *
 * Prefers localStorage so the session survives reloads and is shared across
 * tabs. Trade-off: localStorage is reachable by any successful XSS — keep the
 * app's XSS surface minimal. In-memory was rejected as the default because it
 * signs users out on every reload.
 *
 * Reading `window.localStorage` can throw (storage disabled or blocked, e.g. a
 * privacy mode or a partitioned third-party context). Since this runs at module
 * init, an unguarded throw would crash auth for those users, so fall back to
 * in-memory storage: the app still loads and signs in, the session just doesn't
 * persist across reloads.
 */
function resolveUserStore(): WebStorageStateStore | undefined {
  if (!globalThis.window) {
    return undefined;
  }

  try {
    return new WebStorageStateStore({ store: globalThis.window.localStorage });
  } catch {
    return new WebStorageStateStore({ store: new InMemoryWebStorage() });
  }
}

/** Builds `UserManagerSettings` from neutral inputs (pure — easy to unit test). */
export function buildOidcSettings(inputs: OidcConfigInputs): UserManagerSettings {
  const metadataUrl = inputs.metadataUrl.trim();
  const userStore = resolveUserStore();
  return {
    authority: inputs.authority.trim(),
    ...(metadataUrl ? { metadataUrl } : {}),
    client_id: inputs.clientId.trim(),
    redirect_uri: inputs.appBaseUri,
    post_logout_redirect_uri: inputs.appBaseUri,
    response_type: "code",
    scope: inputs.scope.trim() || "openid profile",
    // Renew proactively from the refresh token before the access token expires.
    automaticSilentRenew: true,
    ...(userStore ? { userStore } : {}),
  };
}

const appBaseUri = globalThis.window ? `${globalThis.window.location.origin}/` : "/";

export const OIDC_ENABLED = isOidcConfigured(
  runtimeConfig.OIDC_AUTHORITY,
  runtimeConfig.OIDC_CLIENT_ID,
);

export const oidcConfig: UserManagerSettings = buildOidcSettings({
  authority: runtimeConfig.OIDC_AUTHORITY,
  metadataUrl: runtimeConfig.OIDC_METADATA_URL,
  clientId: runtimeConfig.OIDC_CLIENT_ID,
  scope: runtimeConfig.OIDC_SCOPE,
  appBaseUri,
});
